import type {
  AutocompleteProps,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@mui/material';
import {
  Autocomplete,
  Box,
  Chip,
  type ChipProps,
  TextField,
  type TextFieldProps,
  Typography,
} from '@mui/material';
import type { CSSProperties, HTMLAttributes } from 'react';
import { combineSx } from '@/utils/combineSx';

const styles = {
  autoCompleteRoot: {},
  autoCompleteStyledChip: {
    height: '18px',
    minWidth: '1px',

    '& .MuiChip-label': {
      paddingLeft: '6px',
      paddingRight: '6px',
    },
    '& .MuiChip-deleteIcon': {
      height: '15px',
      margin: '0 0 0 -1px',
    },
  },
  autoCompletePaper: {
    minWidth: '290px',
    maxWidth: '350px',
  },
  autoCompleteListbox: theme => ({
    fontFamily: theme.fonts?.base,
    fontSize: theme.fontSizes?.base,
    maxHeight: '320px',
    overflowY: 'auto',
    margin: 0,
    padding: 0,
    whiteSpace: 'unset',
  }),
  autoCompleteStyledInput: {
    fontSize: '14px',
    borderRadius: '5px',
    width: '100%',

    '& .MuiInputBase-root': {
      paddingTop: 0,
      width: '100%',
    },

    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      transform: 'translate(12px, 9px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(12px, -5px) scale(0.75)',
      },
    },
    '& .MuiInputLabel-asterisk': {
      color: '#e43246',
    },
  },
  autoCompleteInputSlot: {
    height: '36px',
    borderRadius: '0.25rem',
    display: 'grid',
    gridTemplateColumns: 'minmax(2.5rem, 10rem) 1.5rem',
  },
  autoCompleteHtmlInput: {},
  autoCompleteNote: {
    fontSize: '0.5rem',
    color: 'grey',
    width: '100%',
    padding: '6px 16px',
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#fff',
    borderTop: '1px solid #e0e0e0',
    zIndex: 1,
  },
  extraControls: {
    position: 'sticky',
    bottom: 0,
  },
  clearIndicator: {
    color: '#2a2e49',
  },
  popupIndicator: {
    color: '#2a2e49',
  },
  endAdornment: {},
  startAdornment: {
    position: 'relative',
    top: '2px',
    display: 'flex',
  },
};

const autoCompleteStyles = {
  root: styles.autoCompleteRoot,
  styledTag: styles.autoCompleteStyledChip,
  paper: styles.autoCompletePaper,
  listbox: styles.autoCompleteListbox,
  styledInput: styles.autoCompleteStyledInput,
  inputSlot: styles.autoCompleteInputSlot,
  htmlInput: styles.autoCompleteHtmlInput,
  note: styles.autoCompleteNote,
  extraControls: styles.extraControls,
  clearIndicator: styles.clearIndicator,
  popupIndicator: styles.popupIndicator,
  endAdornment: styles.endAdornment,
  startAdornment: styles.startAdornment,
};

type AutoCompleteSimpleComposableProps<T> = AutocompleteProps<
  T,
  boolean,
  boolean | undefined,
  boolean | undefined
>;

type AutoCompleteOptionProps<T> = HTMLAttributes<HTMLLIElement> & {
  option: T;
  state: AutocompleteRenderOptionState;
  getOptionLabel?: (option: T) => string;
};

type AutoCompleteTagProps<T> = Omit<ChipProps, 'label'> & {
  option: T;
  index: number;
  getTagProps: AutocompleteRenderGetTagProps;
  getOptionLabel?: (option: T) => string;
};

const AutoCompleteTextField = ({ slotProps, ...props }: TextFieldProps) => {
  const mergedSlotProps = {
    input: {
      ...slotProps?.input,
      disableUnderline: true,
    },
    htmlInput: {
      ...slotProps?.htmlInput,
    },
    inputLabel: {
      ...slotProps?.inputLabel,
    },
  };

  return <TextField {...props} slotProps={mergedSlotProps} />;
};

const AutoCompleteOption = <T,>({
  option,
  getOptionLabel,
  ...props
}: AutoCompleteOptionProps<T>) => {
  const label = getOptionLabel
    ? getOptionLabel(option)
    : typeof option === 'string'
      ? option
      : String(option);

  return <li {...props}>{label}</li>;
};

const AutoCompleteTag = <T,>({
  option,
  index,
  getTagProps,
  getOptionLabel,
  ...chipProps
}: AutoCompleteTagProps<T>) => {
  const tagProps = getTagProps({ index });
  const label = getOptionLabel
    ? getOptionLabel(option)
    : typeof option === 'string'
      ? option
      : String(option);

  return <Chip {...tagProps} {...chipProps} label={label} />;
};

type AutoCompleteInputProps = TextFieldProps & {
  params: AutocompleteRenderInputParams;
};

const AutoCompleteInput = ({ params, ...props }: AutoCompleteInputProps) => {
  return (
    <TextField
      {...props}
      slotProps={{
        input: {
          ...params.InputProps,
          disableUnderline: true,
        },
        htmlInput: {
          ...params.inputProps,
        },
        inputLabel: params.InputLabelProps,
      }}
    />
  );
};

const AutoCompleteStyledTag = <T,>({
  option,
  index,
  getTagProps,
  getOptionLabel,
  ...chipProps
}: AutoCompleteTagProps<T>) => {
  const tagProps = getTagProps({ index });
  const label = getOptionLabel
    ? getOptionLabel(option)
    : typeof option === 'string'
      ? option
      : String(option);

  const mergedSx = combineSx(autoCompleteStyles.styledTag, chipProps.sx);

  return <Chip {...tagProps} {...chipProps} label={label} size='small' sx={mergedSx} />;
};

const AutoCompleteStyledInput = ({ params, label, ...restProps }: AutoCompleteInputProps) => {
  const startAdornmentWithWrapper = params.InputProps?.startAdornment ? (
    <Box sx={autoCompleteStyles.startAdornment}>{params.InputProps.startAdornment}</Box>
  ) : undefined;

  const endAdornmentWithWrapper = params.InputProps?.endAdornment ? (
    <Box sx={autoCompleteStyles.endAdornment}>{params.InputProps.endAdornment}</Box>
  ) : undefined;

  return (
    <TextField
      {...restProps}
      label={label}
      variant='filled'
      autoComplete='off'
      sx={combineSx(autoCompleteStyles.styledInput, restProps.sx)}
      slotProps={{
        input: {
          ...params.InputProps,
          ...restProps.slotProps?.input,
          disableUnderline: true,
          startAdornment: startAdornmentWithWrapper,
          endAdornment: endAdornmentWithWrapper,
          sx: combineSx(autoCompleteStyles.inputSlot),
        },
        htmlInput: {
          ...params.inputProps,
          ...restProps.slotProps?.htmlInput,
          style: {},
        },
        inputLabel: params.InputLabelProps,
      }}
    />
  );
};

type AutoCompleteNoteProps = {
  note: string;
  isHidden: boolean;
};

const AutoCompleteNote = ({ note, isHidden }: AutoCompleteNoteProps) => {
  if (isHidden) {
    return null;
  }

  return (
    <li style={autoCompleteStyles.note as CSSProperties}>
      <Typography variant='caption'>{note}</Typography>
    </li>
  );
};

const AutoCompleteSimpleComposable = <T,>({
  value = [],
  multiple = false,
  loading = false,
  options = [],
  size = 'medium',
  disabled = false,
  fullWidth = true,
  renderOption,
  filterOptions,
  renderTags,
  getOptionLabel,
  sx,
  ...restProps
}: AutoCompleteSimpleComposableProps<T>) => {
  return (
    <Autocomplete
      data-id='autocomplete-simple'
      {...restProps}
      value={value}
      options={options}
      loading={loading}
      disabled={disabled}
      size={size}
      fullWidth={fullWidth}
      multiple={multiple}
      filterSelectedOptions={multiple}
      disableCloseOnSelect={true}
      sx={combineSx(autoCompleteStyles.root, sx)}
      {...(filterOptions && { filterOptions })}
      {...(getOptionLabel && { getOptionLabel })}
      {...(renderTags && { renderTags })}
      slotProps={{
        paper: {
          sx: autoCompleteStyles.paper,
        },
        listbox: {
          sx: autoCompleteStyles.listbox,
        },
        clearIndicator: {
          sx: autoCompleteStyles.clearIndicator,
        },
        popupIndicator: {
          sx: autoCompleteStyles.popupIndicator,
        },
      }}
      renderOption={
        renderOption ? renderOption : (props, option) => <li {...props}>{String(option)}</li>
      }
    />
  );
};

AutoCompleteSimpleComposable.TextField = AutoCompleteTextField;
AutoCompleteSimpleComposable.Option = AutoCompleteOption;
AutoCompleteSimpleComposable.Tag = AutoCompleteTag;
AutoCompleteSimpleComposable.Input = AutoCompleteInput;
AutoCompleteSimpleComposable.StyledTag = AutoCompleteStyledTag;
AutoCompleteSimpleComposable.StyledInput = AutoCompleteStyledInput;
AutoCompleteSimpleComposable.Note = AutoCompleteNote;

export { AutoCompleteSimpleComposable };

export type {
  AutoCompleteInputProps,
  AutoCompleteOptionProps,
  AutoCompleteSimpleComposableProps,
  AutoCompleteTagProps,
};
